import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const processed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const hash = window.location.hash;
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) {
      navigate("/login", { replace: true });
      return;
    }
    const session_id = m[1];
    (async () => {
      try {
        const { data } = await api.post("/auth/session", { session_id });
        // Phase 2 fix: brand-new Google sign-ins don't have a workspace yet.
        // The backend returns no_workspace=true so we route them to /signup
        // with their Google profile pre-filled (instead of dumping them on
        // /dashboard with no tenant_id, which used to break every page).
        if (data?.no_workspace) {
          const qs = new URLSearchParams({
            email: data.google_email || "",
            name: data.google_name || "",
            source: "google",
          }).toString();
          window.history.replaceState(null, "", window.location.pathname);
          navigate(`/signup?${qs}`, { replace: true });
          return;
        }
        // Push user into context so ProtectedShell sees it immediately,
        // and clear the URL fragment so a refresh doesn't replay session_id.
        setUser(data);
        window.history.replaceState(null, "", window.location.pathname);
        const isAdmin = data.role === "owner" || data.role === "tenant_admin" || data.platform_superuser;
        navigate(isAdmin ? "/dashboard" : "/calendar", { replace: true, state: { user: data } });
      } catch (e) {
        setError(e?.response?.data?.detail || "Sign-in failed. Please try again.");
      }
    })();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] text-zinc-300">
      <div className="text-center">
        <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">Authenticating</div>
        <div className="font-mono text-amber animate-pulse">VERIFYING SESSION...</div>
        {error && (
          <div className="mt-4">
            <div className="text-red-400 text-sm" data-testid="auth-error">{error}</div>
            <button
              onClick={() => { window.location.href = "/login"; }}
              className="mt-3 text-[10px] uppercase tracking-[0.25em] text-amber hover:text-amber/80 font-mono border border-amber/40 px-3 py-1"
              data-testid="auth-retry-btn"
            >
              Back to sign-in
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
